/**
 * Landing pública (rota `/`) — vitrine de venda do produto. Identidade navy +
 * lime. Única ação: "Acessar sistema" (→ /login). Sem dados reais nem formulário.
 *
 * F-landing-i18n — textos via i18n (FR/EN/PT); idioma inicial auto-detectado
 * do navegador (I18nProvider) e trocável pelo LanguageSwitcher no header.
 */

import { useEffect } from "react";
import { Link, useLocation } from "wouter";
import { LanguageSwitcher } from "../components/common/LanguageSwitcher.js";
import { useAuth } from "../hooks/useAuth.js";
import { useT } from "../i18n/index.js";

function CtaButton({ size = "md", label }: { size?: "md" | "lg"; label: string }) {
  return (
    <Link
      href="/login"
      className={`inline-block rounded-full bg-lime text-on-lime font-semibold transition-transform hover:bg-lime-bright hover:-translate-y-0.5 ${
        size === "lg" ? "px-8 py-3.5 text-base" : "px-6 py-2.5 text-sm"
      }`}
      style={{ boxShadow: "0 0 28px rgba(132, 204, 22, 0.25)" }}
    >
      {label}
    </Link>
  );
}

export default function Landing() {
  const t = useT();
  const year = new Date().getFullYear();
  // F-home-dashboard (CEO 2026-09-16): usuário LOGADO que abre "/" vai direto
  // ao Painel — a vitrine pública fica só para visitantes anônimos.
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  useEffect(() => {
    if (user) setLocation("/dashboard");
  }, [user, setLocation]);

  const features: ReadonlyArray<{ icon: string; title: string; desc: string }> = [
    { icon: "🗂️", title: t("landing.f1t"), desc: t("landing.f1d") },
    { icon: "🧾", title: t("landing.f2t"), desc: t("landing.f2d") },
    { icon: "🏦", title: t("landing.f3t"), desc: t("landing.f3d") },
    { icon: "📊", title: t("landing.f4t"), desc: t("landing.f4d") },
    { icon: "🔁", title: t("landing.f5t"), desc: t("landing.f5d") },
    { icon: "📤", title: t("landing.f6t"), desc: t("landing.f6d") },
    { icon: "🏢", title: t("landing.f7t"), desc: t("landing.f7d") },
    { icon: "🌐", title: t("landing.f8t"), desc: t("landing.f8d") },
  ];
  const benefits: ReadonlyArray<{ icon: string; title: string; desc: string }> = [
    { icon: "⚡", title: t("landing.b1t"), desc: t("landing.b1d") },
    { icon: "🇫🇷", title: t("landing.b2t"), desc: t("landing.b2d") },
    { icon: "🔒", title: t("landing.b3t"), desc: t("landing.b3d") },
  ];

  return (
    <div className="min-h-screen text-neutral-50" style={{ background: "var(--navy, #0b1520)" }}>
      {/* Top bar */}
      <header className="sticky top-0 z-10 backdrop-blur border-b border-white/5">
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between gap-3">
          <span className="text-lg font-bold tracking-tight">
            Storm <span className="text-lime">Gestion</span>
          </span>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <CtaButton label={t("landing.navCta")} />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-96 w-[42rem] rounded-full blur-3xl opacity-20"
          style={{ background: "radial-gradient(closest-side, #84cc16, transparent)" }}
        />
        <div className="relative mx-auto max-w-4xl px-6 pt-20 pb-16 text-center">
          <img
            src="/euro-storm-logo.svg"
            alt="Euro Storm"
            className="mx-auto mb-8 h-auto"
            style={{
              width: "100%",
              maxWidth: 300,
              filter: "drop-shadow(0 0 24px rgba(132,204,22,.15))",
            }}
          />
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-lime">
            {t("landing.heroEyebrow")}
          </p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            {t("landing.heroTitlePre")}{" "}
            <span className="text-lime">{t("landing.heroTitleAccent")}</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base sm:text-lg text-neutral-300 leading-relaxed">
            {t("landing.heroSubtitle")}
          </p>
          <div className="mt-9">
            <CtaButton size="lg" label={t("landing.navCta")} />
          </div>
          {/* F-tech-signup — porta do técnico. Discreta, mas tem de existir:
              a página pública não serve de nada se ninguém a encontra. */}
          <div className="mt-5">
            <Link
              href="/cadastro-tecnico"
              className="text-sm text-neutral-400 underline-offset-4 hover:text-lime hover:underline"
            >
              {t("landing.techSignupLink")}
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <h2 className="text-center text-2xl font-bold">{t("landing.featuresTitle")}</h2>
        <p className="mx-auto mt-2 mb-10 max-w-2xl text-center text-sm text-neutral-400">
          {t("landing.featuresSubtitle")}
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f) => (
            <div
              key={f.title}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-lime/40"
            >
              <div className="text-2xl">{f.icon}</div>
              <h3 className="mt-3 font-semibold text-neutral-100">{f.title}</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-neutral-400">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="border-y border-white/5 bg-white/[0.02]">
        <div className="mx-auto max-w-6xl px-6 py-14 grid gap-8 sm:grid-cols-3">
          {benefits.map((b) => (
            <div key={b.title}>
              <div className="text-3xl">{b.icon}</div>
              <h3 className="mt-3 text-lg font-semibold">{b.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-neutral-400">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-3xl px-6 py-20 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold">
          {t("landing.finalTitlePre")}{" "}
          <span className="text-lime">{t("landing.finalTitleAccent")}</span>
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-sm text-neutral-400">
          {t("landing.finalSubtitle")}
        </p>
        <div className="mt-8">
          <CtaButton size="lg" label={t("landing.navCta")} />
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5">
        <div className="mx-auto max-w-6xl px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-neutral-500">
          <span>
            Storm <span className="text-lime">Gestion</span> · Grupo Storm
          </span>
          <span>
            © {year} Euro Storm Débosselage · {t("landing.footerRights")}
          </span>
        </div>
      </footer>
    </div>
  );
}
